import { useEffect, useRef, useState } from 'react';

import {
  ILoyaltyGenerateOtpMutationVariables,
  useLoyaltyGenerateOtpMutation,
} from 'generated/graphql-gateway';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { StorageKeys } from 'utils/local-storage';

import { SHORT_CODE_EXPIRATION_MINUTES } from './constants';
import { IShortCodeData, IUserShortCode, ShortCodeState } from './types';
import { calculateExpiry, differenceMsFromNow, isExpired } from './utils';

/**
 * useShortCode holds otp short-code and its state.
 */
export const useShortCode = (): IUserShortCode => {
  const [generateCodeOtp] = useLoyaltyGenerateOtpMutation();
  const generateCodeOtpTimeoutRef = useRef<number | null>();
  const abortControllerRef = useRef<AbortController>();
  const [shortCodeLoading, setShortCodeLoading] = useState<boolean>(false);
  const [{ shortCode, expiry }, setShortCodeData, clearShortCodeData] = useLocalStorageState<
    IShortCodeData
  >({
    key: StorageKeys.IN_RESTAURANT_REDEMPTION_SHORT_CODE,
    defaultReturnValue: {},
  });
  const [shortCodeState, setShortCodeState] = useState<ShortCodeState>(() =>
    shortCode ? ShortCodeState.Pending : ShortCodeState.None
  );

  const setShortCode = (newShortCode: string | undefined) => {
    setShortCodeData({
      shortCode: newShortCode,
      expiry: newShortCode && calculateExpiry(SHORT_CODE_EXPIRATION_MINUTES),
    });

    if (newShortCode) {
      setShortCodeState(ShortCodeState.Pending);
    }
  };
  const setRequestError = () => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.RequestError);
  };
  const setShortCodeClaimed = () => {
    setShortCodeState(ShortCodeState.Claimed);
  };

  const resetShortCode = () => {
    clearShortCodeData();
    setShortCodeState(ShortCodeState.None);
    setShortCodeLoading(false);
  };

  /**
   * getNewShortCode works as a debounce function in order to prevent running
   * multiple mutations at the same time
   */
  const getNewShortCode = async (params: ILoyaltyGenerateOtpMutationVariables) => {
    if (generateCodeOtpTimeoutRef.current) {
      clearTimeout(generateCodeOtpTimeoutRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    setShortCodeLoading(true);
    generateCodeOtpTimeoutRef.current = setTimeout(async () => {
      abortControllerRef.current = window.AbortController
        ? new window.AbortController()
        : undefined;

      let response;
      try {
        response = await generateCodeOtp({
          variables: params,
          context: {
            fetchOptions: {
              signal: abortControllerRef.current?.signal,
            },
          },
        });
        setShortCode(response?.data?.generateOtp.password);
      } catch (e) {
        setRequestError();
      } finally {
        generateCodeOtpTimeoutRef.current = null;
        setShortCodeLoading(false);
      }
    }, 250);
  };

  useEffect(() => {
    if (!expiry) {
      return;
    } else if (isExpired(expiry)) {
      setShortCodeState(ShortCodeState.Expired);
      return;
    }

    const codeDurationMilliseconds = differenceMsFromNow(expiry);
    const timeoutId = setTimeout(() => {
      setShortCodeState(ShortCodeState.Expired);
    }, codeDurationMilliseconds);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [expiry]);

  return {
    resetShortCode,
    getNewShortCode,
    setShortCodeClaimed,
    shortCode,
    shortCodeLoading,
    shortCodeState,
  };
};
